import { Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useMyFields } from '/@/hooks/fields';
import { useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import NewFieldGrowersModal from '/@/views/dashboard/growers/components/new-field-modal';
import FieldSelectorModal from '../../../../../../views/dashboard/growers/components/field-selector-modal';

const CreateVisitModal = ({
    open,
    setOpen,
    field,
    userNodeId,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    field?: Field;
    userNodeId: number;
}) => {
    const [openCreateFieldModal, setOpenCreateFieldModal] = useState(false);
    const country = useSelector(getSessionCountry);
    const ownUserNode = useOwnUserNode();
    const location = useLocation();

    // Construct return URL with openVisitReport param for field creation flow
    const getReturnUrl = () => {
        const params = new URLSearchParams(location.search);
        params.set('openVisitReport', 'true');
        return `${location.pathname}?${params.toString()}`;
    };
    const allFieldsQuery = useMyFields({
        page: 1,
        pageSize: 1,
        country: country.code,
        selectOnly: 'id',
        userNodeId,
        includeChildren: false,
    });
    const navigate = useNavigate();

    const isOwnUserNode = ownUserNode.data?.id === userNodeId;

    if (allFieldsQuery.isValidating) return null;

    if (!allFieldsQuery.data?.count) {
        return (
            <>
                <ModalV2
                    open={open}
                    onClose={() => setOpen(false)}
                    title={_('Agregá un lote para empezar')}
                    actions={
                        <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ width: '100%' }}>
                            <Button
                                variant='text'
                                color='secondary'
                                onClick={() => {
                                    setOpen(false);
                                }}
                            >
                                {_('Cancelar')}
                            </Button>
                            <Button variant='contained' color='secondary' onClick={() => setOpenCreateFieldModal(true)}>
                                {_('Agregar lote')}
                            </Button>
                        </Stack>
                    }
                >
                    <Stack spacing={2} width='100%'>
                        <Typography variant='subtitle1' color='primary' textAlign='center'>
                            {isOwnUserNode
                                ? _(
                                      'Para registrar observaciones y generar el informe de visita, primero necesitás agregar un lote. Una vez creado, vas a poder cargar los datos de la visita.'
                                  )
                                : _(
                                      'Para registrar observaciones y generar el informe de visita, primero necesitás agregar un lote de tu productor. Una vez creado, vas a poder cargar los datos de la visita.'
                                  )}
                        </Typography>
                    </Stack>
                </ModalV2>
                <NewFieldGrowersModal
                    open={openCreateFieldModal}
                    setOpen={setOpenCreateFieldModal}
                    growerUserNodeId={userNodeId}
                    trackEntrySource={'visit-report'}
                    returnUrl={getReturnUrl()}
                />
            </>
        );
    }

    return (
        <FieldSelectorModal
            ModalV2Props={{
                open: open,
                onClose: () => setOpen(false),
                title: _('Seleccioná los lotes que visitaste'),
            }}
            text={_(
                'Vas a registrar observaciones, notas, subir fotos e incluir gráficos o datos relevantes de cada visita al lote'
            )}
            userNodeId={userNodeId}
            action={{
                onClick: (fieldIds: number[]) => {
                    navigate(
                        reverse('visitReport.VisitReportEdit', {
                            qs: {
                                fieldIds: fieldIds.join(','),
                            },
                        })
                    );
                },
                text: _('Empezar informe'),
            }}
            field={field}
        />
    );
};

export default CreateVisitModal;
